import { createListenerMiddleware } from '@reduxjs/toolkit';

import { gameCommunicationService } from '../../services/gameCommunication/GameCommunicationService';
import { rawTotalBet } from '../../utils/bets';
import type { CoreRootState } from '../CoreStore';
import { autoplayActions } from '../slices/autoplay/slice';
import { AutoplayStatus } from '../slices/autoplay/types';
import { configActions } from '../slices/config/slice';
import { GameStatus } from '../slices/game/types';
import { toastActions } from '../slices/toast/slice';

// This middleware listens for game visibility change
export const visibilityMiddleware = createListenerMiddleware<CoreRootState>();

export const isBetsOpenStatusSelector = (state: CoreRootState): boolean => {
  return (
    state.gameState.currentStatus === GameStatus.PlaceYourBets || state.gameState.currentStatus === GameStatus.LastBets
  );
};

visibilityMiddleware.startListening({
  actionCreator: configActions.setGameVisible,
  effect: (_action, { dispatch, getState }) => {
    const state = getState();
    const { roundsLeft, autoplayStatus } = state.autoplay;
    const isBetsOpenStatus = isBetsOpenStatusSelector(state);
    const { betStack } = state.bets;
    const totalBet = betStack.length > 0 ? rawTotalBet(betStack[betStack.length - 1]?.betSpots) : 0;

    if (state.config.isGameVisible) {
      if (roundsLeft > 0) {
        dispatch(autoplayActions.resume());
        /**
         * We must check against old state because in this callback the dispatch will have happened but the callback will not have received updated values at the time of execution.
         * That is why we check if it has been paused or running. If we get Stopped, we don't want to bet.
         */
        if (
          (autoplayStatus === AutoplayStatus.Paused || autoplayStatus === AutoplayStatus.Running) &&
          gameCommunicationService.isAlive
        ) {
          /**
           * Place bets manually in case the AP is unpaused during open bets countdown and have
           * missed the chance to place the bet on bets open start.
           * Additionally checks if game socket connection is still alive, to avoid placing bets when this cannot be communicated to the server.
           * */
          if (isBetsOpenStatus && totalBet === 0) {
            dispatch(autoplayActions.setPendingAutoplayBet(true));
          }
        }
      } else {
        /**
         * We must handle case where the game becomes visible during bets open phase after long enough time has passed to lose WS connection.
         * In such a case the check on win results processor might be missed that stops the auotplay at 0 rounds left.
         * Therefore we must check if rounds left is 0 and if so, stop the autoplay here.
         */
        if (autoplayStatus === AutoplayStatus.Paused || autoplayStatus === AutoplayStatus.Running) {
          dispatch(autoplayActions.stop());
          dispatch(toastActions.createToast('AUTOPLAY_COMPLETED'));
        }
      }
    } else {
      dispatch(autoplayActions.pause());
    }
  },
});
